/**
 * Jovii — CHAT AND SOCIAL, the shared vocabulary.
 *
 * WHY THIS FILE EXISTS AND NOT `$types`
 *
 * `web/src/lib/types.ts` is the SCAFFOLD agent's file and six agents were editing the
 * tree at once, so the chat/social protocol was given a home of its own rather than
 * appended to a file nobody in this phase owns. It works exactly the same way `$types`
 * does: the web app imports it as `$lib/social/protocol`, and the server imports it by
 * relative path (`../../web/src/lib/social/protocol.ts`) — which is precisely what
 * `internal/rooms/hub.ts` already does with `$types` through an alias.
 *
 * When somebody consolidates, everything below moves into `$types` verbatim and this
 * file becomes a re-export. Nothing here contradicts anything there; the message types
 * are additive and the server dispatch treats them as a second union alongside
 * `ClientMessage` / `ServerMessage`.
 *
 * RULES, THE SAME AS `$types`
 *  - Types only, plus the handful of caps and pure helpers both ends need.
 *  - Every field carries its unit and its WRITER.
 *  - Additive changes only.
 *
 * THE ONE TRANSPORT RULE. Chat rides the room WebSocket. There is no second socket, no
 * long-poll and no SSE stream: a second transport would be a second thing to reconnect,
 * a second thing to authenticate and a second thing that iOS can quietly kill while the
 * page still looks alive. See `docs/SOCIAL.md`.
 */

import type { MemberId, RoomId, ServerTimeMs } from '$types';

/* ────────────────────────────────────────────────────────────────────────────
 * 1. Identity
 * ──────────────────────────────────────────────────────────────────────────── */

/** Server-owned account identity. Stable across devices only if the same account key
 *  is presented; see `docs/SOCIAL.md` § "Who is this". */
export type UserId = string;

/**
 * A handle is the human-typeable name people search each other by: lowercase letters,
 * digits and single hyphens, 3–24 characters, e.g. `amber-otter`.
 *
 * It is generated for you, unique, and changeable at most once every
 * `HANDLE_CHANGE_COOLDOWN_MS`. That limit is enforced in `internal/social/store.ts`,
 * NOT in the UI — a rule that only exists in a form field is not a rule.
 */
export type Handle = string;

/** An avatar is a CHOICE FROM A SET, never an upload. See `AVATARS`. */
export type AvatarId = string;

/** The public face of an account: what a friend, or a room, is allowed to see. */
export interface PublicProfile {
  userId: UserId;
  handle: Handle;
  /** Display name. Free text, 1–32 chars, control characters stripped. */
  displayName: string;
  avatar: AvatarId;
  /** Stable label colour derived from `userId`, so the same person is the same colour
   *  in the member list, the chat and the friends list. CSS hex. */
  color: string;
}

/** Your own account, including the parts nobody else sees. */
export interface SelfProfile extends PublicProfile {
  /** Server-owned preference; absent stored values migrate to true. */
  privateMode: boolean;
  createdAt: ServerTimeMs;
  /**
   * When the handle was last changed, or **0 when it never has been**.
   *
   * The first change is free: you were given a random name and choosing your own is the
   * whole point. The once-a-month limit applies from that first change onwards.
   */
  handleChangedAt: ServerTimeMs;
  /** 0 when a change is allowed right now; otherwise how long is left, milliseconds. */
  handleChangeInMs: number;
}

/**
 * The 24 avatars. Ids, not images: the client draws them, so there is no upload path,
 * no image store, no moderation problem and no bytes on the wire.
 *
 * Keep this list append-only — a stored avatar id that vanishes leaves somebody blank.
 */
export const AVATARS: readonly AvatarId[] = [
  'otter', 'fox', 'owl', 'moth', 'crane', 'ibex', 'lynx', 'newt',
  'moon', 'comet', 'orbit', 'prism', 'ember', 'frost', 'tide', 'dune',
  'fern', 'clove', 'plum', 'thyme', 'quartz', 'onyx', 'amber', 'jade'
] as const;

export function isAvatarId(v: unknown): v is AvatarId {
  return typeof v === 'string' && (AVATARS as readonly string[]).includes(v);
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2. Friends
 * ──────────────────────────────────────────────────────────────────────────── */

export type FriendRequestId = string;

export interface FriendRequest {
  id: FriendRequestId;
  from: PublicProfile;
  to: PublicProfile;
  createdAt: ServerTimeMs;
}

/** What a friend is up to. Absent fields mean "not in a room" or "nothing pinned yet". */
export interface FriendPresence {
  userId: UserId;
  handle: Handle;
  displayName: string;
  avatar: AvatarId;
  color: string;
  /** True when at least one live socket belongs to this account. */
  online: boolean;
  /**
   * True when a device of theirs HAS A PLAYER ATTACHED — paused counts, browsing
   * does not. This is what licenses the words "watching The Matrix"; `roomId` and
   * `title` alone never did.
   *
   * **Server contract.** `RoomHub.watchingByUser()` must set this from the member's
   * own `state`, and only these four count:
   *
   * ```ts
   * const WATCHING = new Set(['buffering', 'ready', 'playing', 'stalled']);
   * out.watching = WATCHING.has(m.state);
   * ```
   *
   * `idle` is excluded because it is exactly what a member who has left the watch
   * screen reports (measured: an in-app navigation away from the player put
   * `ready state=idle` on the wire 0.5 s later, once a second for the next 50 s,
   * echoed back by the server every time).
   *
   * `probing` is excluded for a second, uglier reason: a tab that RELOADS onto a
   * non-player screen while it still remembers a room reports `probing` for ever,
   * because `initialSyncState()` seeds `mediaAttached: true` and nothing on a
   * screen without a player ever sets it false. Measured: 39 s of
   * `ready state=probing` from a browser sitting on `/`. Until that is fixed in
   * `web/src/lib/sync/`, treating `probing` as watching would leave this defect
   * half-alive. `error` is excluded because a player that failed is not one that
   * is playing.
   *
   * Absent means an older server that has not been taught this. The client then
   * falls back to the old meaning rather than blanking everybody — see
   * `presence-line.ts`.
   */
  watching?: boolean;
  /** The room they are in, when they are in one. */
  roomId?: RoomId;
  /** What that room has pinned, for "watching The Matrix". */
  title?: string;
  /** IMDb id of the same, so the UI can link to it. */
  imdbId?: string;
  /** 'movie' | 'series'. */
  kind?: string;
  /** Server time this was true. */
  at: ServerTimeMs;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 4. Chat
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * One line of room chat.
 *
 * `memberId` is who said it *in this room*; `userId` is the account behind them, which
 * is absent for anybody who has not got one yet (an old client, or a browser that lost
 * its account key). Chat works without an account — it degrades to the room display
 * name — because a chat that stops working when identity does is a chat nobody trusts.
 */
export interface ChatMessage {
  id: string;
  roomId: RoomId;
  memberId: MemberId;
  /** The account behind the member, when there is one. */
  userId?: UserId;
  handle?: Handle;
  /** Display name AT THE TIME OF SENDING. Deliberately denormalised: a rename must not
   *  rewrite history, and the backlog must be renderable with no other lookups. */
  name: string;
  /** The member's room colour, same reason. CSS hex. */
  color: string;
  /** 1–`CHAT_MAX_TEXT` characters, control characters stripped. */
  text: string;
  /** Server clock at acceptance. */
  at: ServerTimeMs;
  /** Echo of the sender's `nonce`, so a client can retire its optimistic copy. */
  nonce?: string;
  /** Set on server-authored lines (joins, leaves). Absent for people. */
  system?: boolean;
}

/** Longest message we accept. Anything longer is refused, never truncated: a silently
 *  cut sentence is worse than an error. */
export const CHAT_MAX_TEXT = 2000;

/** Browser transcript count cap. Peer relay byte limits live in chat/protocol.ts.
 * The server does not retain transcripts. */
export const CHAT_BACKLOG_MAX = 200;

/* ────────────────────────────────────────────────────────────────────────────
 * 5. Wire messages — client → server
 * ──────────────────────────────────────────────────────────────────────────── */

/** Say something in the room you are in. */
export interface CChat {
  t: 'chat';
  roomId: RoomId;
  text: string;
  /** Client-generated id echoed back on the broadcast, so an optimistic bubble can be
   *  reconciled instead of duplicated. Max 64 chars. */
  nonce?: string;
}

/** Ask for the backlog again (a reconnect gets it unasked, on `join`). */
export interface CChatHistory {
  t: 'chatHistory';
  roomId: RoomId;
}

/** Subscribe (or unsubscribe) this socket to friend-presence pushes. */
export interface CFriendsWatch {
  t: 'friendsWatch';
  on: boolean;
}

/** Ask a friend to let you into the room they are in. */
export interface CAskToJoin {
  t: 'askToJoin';
  /** The friend's account. Must actually be a friend; the server checks. */
  to: UserId;
}

/** Answer somebody who asked to join your room. */
export interface CJoinAnswer {
  t: 'joinAnswer';
  requestId: string;
  accept: boolean;
}

export type SocialClientMessage = CChat | CChatHistory | CFriendsWatch | CAskToJoin | CJoinAnswer;

/* ────────────────────────────────────────────────────────────────────────────
 * 6. Wire messages — server → client
 * ──────────────────────────────────────────────────────────────────────────── */

/** One new line, broadcast to the whole room including the sender. */
export interface SChatMessage {
  t: 'chatMessage';
  roomId: RoomId;
  message: ChatMessage;
}

/** The recent history, oldest first. Sent unasked on join and on re-attach. */
export interface SChatBacklog {
  t: 'chatBacklog';
  roomId: RoomId;
  messages: ChatMessage[];
  /** How many are kept at most, so the UI can say "earlier messages are gone". */
  cap: number;
  /** True when older messages were dropped to stay under the cap. */
  truncated: boolean;
}

/** Friends' presence, pushed to a socket that asked with `friendsWatch`. Always the
 *  FULL list — it is small, and a full list cannot drift out of sync with reality. */
export interface SFriendPresence {
  t: 'friendPresence';
  friends: FriendPresence[];
  at: ServerTimeMs;
}

/** Somebody wants into your room. Show a prompt; answer with `joinAnswer`. */
export interface SJoinRequest {
  t: 'joinRequest';
  requestId: string;
  from: PublicProfile;
  roomId: RoomId;
  /** Server time after which the asker has stopped waiting. */
  expiresAt: ServerTimeMs;
}

/** The answer, sent back to whoever asked. */
export interface SJoinAnswered {
  t: 'joinAnswered';
  requestId: string;
  accepted: boolean;
  /** Present and joinable only when `accepted`. */
  roomId?: RoomId;
  /** Who answered, for the toast. */
  by?: PublicProfile;
  /** Why not, when it was not a person saying no: 'expired' | 'offline' | 'no-room'. */
  reason?: string;
}

export interface RoomInvitation {
  id: string;
  roomId: RoomId;
  from: PublicProfile;
  expiresAt: ServerTimeMs;
}

export interface SRoomInvites {
  t: 'roomInvites';
  invites: RoomInvitation[];
}

export type SocialServerMessage =
  | SRoomInvites
  | SChatMessage
  | SChatBacklog
  | SFriendPresence
  | SJoinRequest
  | SJoinAnswered;

export type SocialMessage = SocialClientMessage | SocialServerMessage;

/** How long an unanswered join request stands before it is dropped. */
export const JOIN_REQUEST_TTL_MS = 60_000;

/* ────────────────────────────────────────────────────────────────────────────
 * 7. Rate limits — the floor, present from the first commit
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * WHY THESE EXIST BEFORE ANYBODY COMPLAINED.
 *
 * `docs/OPEN-DEFECTS.md` #2 is the room server sending 9,735 `ready` frames at a 1 ms
 * median gap because a floor was consulted only on the path nobody took. The fix was a
 * hard floor under *every* effect. Chat is a strictly easier problem — a human types
 * at most one line every second or two — so it starts with the floor already in place,
 * per member AND per room, and an over-limit send is DROPPED, never a disconnect: the
 * usual cause of a flood is a retry loop, and closing the socket makes it retry harder.
 *
 * Per member: 5 in hand, one back every 1.5 s. Enough to paste four quick reactions;
 * not enough to be a channel.
 * Per room: 30 in hand, 5 a second, so eight people all reacting at once still get
 * through and a single looping client cannot fill anybody's backlog.
 */
export const CHAT_RATE_BURST = 5;
export const CHAT_RATE_PER_SEC = 1 / 1.5;
export const CHAT_ROOM_RATE_BURST = 30;
export const CHAT_ROOM_RATE_PER_SEC = 5;

/* ────────────────────────────────────────────────────────────────────────────
 * 8. Shared helpers (both ends need the identical answer)
 * ──────────────────────────────────────────────────────────────────────────── */

/** Handles are compared, stored and displayed in this one canonical form. */
export function normaliseHandle(raw: unknown): Handle | null {
  if (typeof raw !== 'string') return null;
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/^@+/, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
  if (s.length < 3 || s.length > 24) return null;
  return s;
}

/**
 * Clean a chat line. Returns null when there is nothing left to send.
 *
 * Control characters go (they break the log format and terminal readers), a run of
 * newlines collapses to two, and the length is checked AFTER cleaning so a message made
 * entirely of padding cannot slip past.
 */
export function cleanChatText(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // Written as a loop rather than a character class on purpose: a regex holding literal
  // control characters is a source file that does not survive being copied about.
  let stripped = '';
  for (const ch of raw.replace(/\r\n?/g, '\n')) {
    const c = ch.codePointAt(0) ?? 0;
    // Keep the newline we just normalised; drop every other C0 control and DEL. A raw
    // NUL in a log line is how a chat message becomes a log injection.
    if (c === 0x0a || (c >= 0x20 && c !== 0x7f)) stripped += ch;
  }
  const s = stripped
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+$/gm, '')
    .trim();
  if (s === '' || s.length > CHAT_MAX_TEXT) return null;
  return s;
}

/** Narrow an inbound frame to a social message without validating its payload. */
export function isSocialMessage(v: unknown): v is SocialMessage {
  if (typeof v !== 'object' || v === null) return false;
  const t = (v as { t?: unknown }).t;
  return (
    t === 'chat' ||
    t === 'chatHistory' ||
    t === 'friendsWatch' ||
    t === 'askToJoin' ||
    t === 'joinAnswer' ||
    t === 'chatMessage' ||
    t === 'chatBacklog' ||
    t === 'friendPresence' ||
    t === 'joinRequest' ||
    t === 'joinAnswered' ||
    t === 'roomInvites'
  );
}
